import {
  useCallback,
  useMemo,
  useState,
} from "react";
import type { DrawerFilters } from "./Filter.context";
import FiltersDrawerContext from "./FiltersDrawer.context";
import {
  getStorageItem,
  setStorageItem,
  STORAGE_KEY_FILTERS,
} from "@shared/utils/localStorage";

const defaultFilters: DrawerFilters = {
  status: [],
  channels: [],
  critical: [],
};

const getInitialFilters = (): DrawerFilters => {
  const stored = getStorageItem<DrawerFilters>(STORAGE_KEY_FILTERS, defaultFilters);
  
  if (!stored || typeof stored !== "object") {
    return defaultFilters;
  }

  const status = Array.isArray(stored.status) ? stored.status : [];
  const channels = Array.isArray(stored.channels) ? stored.channels : [];
  const critical = [];

  return { status, channels, critical };
};

const FiltersDrawerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [drawerFiltersState, setDrawerFiltersState] = useState<DrawerFilters>(getInitialFilters);

  const setDrawerFilters = useCallback((value: React.SetStateAction<DrawerFilters>) => {
    setDrawerFiltersState((prev) => {
      let next: DrawerFilters;
      if (typeof value === "function") {
        next = value(prev);
      } else {
        next = value;
      }

      setStorageItem(STORAGE_KEY_FILTERS, next);

      return next;
    });
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const contextValue = useMemo(() => ({
    open,
    onClose,
    onOpen,
    drawerFilters: drawerFiltersState,
    setDrawerFilters,
  }), [open, onClose, onOpen, drawerFiltersState, setDrawerFilters]);

  return (
    <FiltersDrawerContext.Provider value={contextValue}>
      {children}
    </FiltersDrawerContext.Provider>
  );
};

export default FiltersDrawerProvider;
